import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Alert, ArrowRight, Badge, Button, Copy01, DotsVertical, LayersThree01, Menu, Modal, Trash01,
} from "@opendate-app/setlist";

import { useBroadcastEditContext } from "../BroadcastEditContainer";
import SaveAsBlueprintModal from "../SaveAsBlueprintModal";
import { BuilderIdentity, ProgressTrack } from "../../shared/BuilderHeader";
import { toastError } from "../../../../shared/toastHelper";
import InboxPreview from "./InboxPreview";
import RecipientsStep from "./RecipientsStep";
import ContentStep from "./ContentStep";
import SenderStep from "./SenderStep";
import ScheduleStep, { deliverySummary } from "./ScheduleStep";
import FailedSendAlert from "./FailedSendAlert";
import { offsetDateToTimezone } from "../../../../shared/timeZoneLogic";
import MessageStep from "./MessageStep";
import PhonePreview from "./PhonePreview";
import useRenderedSms from "./useRenderedSms";
import { smsSegments } from "./smsSegments";
import { senderLabel } from "./smsMessage";
import { composerSteps, completedComposerSteps, initialComposerStep, readyToSend } from "./composerSteps";

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    minHeight: 560,
    overflow: "hidden",
    background: "var(--sl-bg-primary)",
    color: "var(--sl-text-primary)",
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: "var(--sl-radius-xl)",
  },
  header: {
    minHeight: 72,
    flexShrink: 0,
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px 16px",
    padding: "8px 20px",
    borderBottom: "1px solid var(--sl-border-secondary)",
  },
  identity: {
    flex: "1 1 0",
    minWidth: 260,
  },
  actions: {
    flex: "1 1 0",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 8,
  },
  body: {
    flex: 1,
    display: "flex",
    minHeight: 0,
  },
  formColumn: {
    width: 520,
    flexShrink: 0,
    overflowY: "auto",
    padding: 32,
    borderRight: "1px solid var(--sl-border-secondary)",
  },
  contentArea: {
    flex: 1,
    overflowY: "auto",
  },
  fullWidth: {
    flex: 1,
    overflowY: "auto",
    padding: 32,
  },
  previewPane: {
    flex: 1,
    overflowY: "auto",
    background: "var(--sl-bg-tertiary)",
  },
  title: {
    margin: 0,
    fontSize: 20,
    fontWeight: 600,
    color: "var(--sl-text-primary)",
  },
  sendSummary: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    columnGap: 16,
    rowGap: 6,
    margin: 0,
    fontSize: 14,
    color: "var(--sl-text-secondary)",
  },
  subtitle: {
    margin: "4px 0 0",
    fontSize: 14,
    color: "var(--sl-text-tertiary)",
  },
};

const BroadcastComposer = () => {
  const {
    csrfToken,
    team,
    venueOwnership,
    broadcast,
    updateBroadcast,
    waitForBroadcastSaves,
    recipientsCount,
    emailSendDomain,
    selectedSendDomain,
    templateHasContent,
    checkTemplateHasContent,
    scheduledSend,
    scheduledAt,
    timeZone,
    setTimeZone,
    scheduleBroadcast,
    formatDate,
    template,
    createBlueprint,
    updateBlueprint,
    isEmailTemplate,
    twilioMessagingServices,
  } = useBroadcastEditContext();

  const isSms = !isEmailTemplate;
  const steps = composerSteps(isSms);
  const trackSteps = steps.map((item) => (
    item.value === "recipients"
      ? { ...item, badge: <Badge color="gray" size="sm" aria-label={`${recipientsCount} recipients`}>{recipientsCount}</Badge> }
      : item
  ));
  const [step, setStep] = useState(initialComposerStep(window.location.search, isSms));
  const [draft, setDraft] = useState(broadcast.twilio_template?.template || "");
  const rendered = useRenderedSms(isSms ? draft : "");
  const flushSmsSave = useRef(null);
  const savedSmsTemplate = useRef(null);
  savedSmsTemplate.current = broadcast.twilio_template?.template || "";
  const [name, setName] = useState(broadcast.name);
  const [editedSenderNames, setEditedSenderNames] = useState(null);
  const [delivery, setDelivery] = useState("later");
  const [confirmingSend, setConfirmingSend] = useState(false);
  const pendingSend = useRef(null);
  const [sentMessage, setSentMessage] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [failedSend, setFailedSend] = useState(broadcast.failed_send);
  const [savingBlueprint, setSavingBlueprint] = useState(false);
  const pageRef = useRef(null);
  const [pageHeight, setPageHeight] = useState(null);

  useLayoutEffect(() => {
    const fitToViewport = () => {
      setPageHeight(window.innerHeight - pageRef.current.getBoundingClientRect().top - window.scrollY - 16);
    };

    fitToViewport();
    window.addEventListener("resize", fitToViewport);
    return () => window.removeEventListener("resize", fitToViewport);
  }, []);

  useEffect(() => {
    checkTemplateHasContent();
    if (!scheduledSend && window.gon?.timeZone) setTimeZone(window.gon.timeZone);
  }, []);

  useEffect(() => {
    if (scheduledSend) setFailedSend(null);
  }, [scheduledSend]);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("step", step);
    window.history.replaceState(null, "", url);
  }, [step]);

  const completed = completedComposerSteps({
    isSms,
    recipientsCount,
    emailSendDomain,
    selectedSendDomain,
    subject: broadcast.subject,
    templateHasContent,
    scheduledSend,
    twilioTemplate: broadcast.twilio_template,
    messageDraft: draft,
    messageErrors: isSms ? rendered.errors : [],
  });

  const stepIndex = steps.findIndex((composerStep) => composerStep.value === step);
  const currentStep = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;
  const nextDisabled = isSms
    ? step === "message" && !completed.message
    : step === "content" && !broadcast.beefree_template?.template;
  const canSend = readyToSend(completed, delivery, scheduledAt) && (!scheduledSend || scheduledSend.state === "scheduled");
  const deliveryText = deliverySummary({ delivery, scheduledAt, formatDate, timeZone }).text;

  const send = () => {
    const at = delivery === "now" ? offsetDateToTimezone(new Date(), timeZone) : scheduledAt;
    const schedule = () => scheduleBroadcast(() => {
      setSentMessage(delivery === "now"
        ? `Your broadcast is on its way to ${recipientsCount} fans.`
        : `Broadcast scheduled for ${deliveryText} to ${recipientsCount} fans.`);
    }, at);

    if (!isSms) {
      setConfirmingSend(false);
      schedule();
      return;
    }

    const thisSend = {};
    pendingSend.current = thisSend;

    waitForBroadcastSaves().then(() => {
      if (pendingSend.current !== thisSend) return;
      pendingSend.current = null;

      if (savedSmsTemplate.current !== draft) {
        toastError("Your latest message didn't save, so nothing was sent. Please try again.");
        updateBroadcast({
          twilio_template_attributes: {
            id: broadcast.twilio_template.id,
            template: draft,
            twilio_messaging_service_id: broadcast.twilio_template.twilio_messaging_service_id,
          },
        }, false);
        return;
      }

      setConfirmingSend(false);
      schedule();
    });
  };

  const cancelSend = () => {
    pendingSend.current = null;
    setConfirmingSend(false);
  };

  const saveName = () => {
    const trimmed = name.trim();

    if (!trimmed) {
      setName(broadcast.name);
      return;
    }

    if (trimmed !== broadcast.name) {
      updateBroadcast({ broadcast: { name: trimmed } }, false);
    }
  };

  const duplicate = () => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    flushSmsSave.current?.();
    waitForBroadcastSaves()
      .then(() => axios.post(`/teams/${team.id}/broadcasts/${broadcast.id}/duplicate`))
      .then(({ data }) => { window.location.href = `/teams/${team.id}/broadcasts/${data.id}/edit`; })
      .catch(() => toastError("Could not duplicate the broadcast, please try again"));
  };

  const destroy = () => {
    setConfirmingDelete(false);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.delete(`/teams/${team.id}/broadcasts/${broadcast.id}`)
      .then(() => { window.location.href = `/teams/${team.id}/broadcasts`; })
      .catch((error) => {
        const errors = error.response?.data?.errors;
        toastError(errors?.length ? errors.join(", ") : "Could not delete the broadcast, please try again");
      });
  };

  const stepHeading = (
    <div>
      <h1 style={styles.title}>{currentStep.title}</h1>
      <p style={styles.subtitle}>{currentStep.subtitle}</p>
    </div>
  );

  return (
    <div ref={pageRef} style={{ ...styles.page, height: pageHeight }}>
      <header style={styles.header}>
        <div style={styles.identity}>
          <BuilderIdentity
            noun="broadcast"
            caption={`${isSms ? "SMS broadcast" : "Email broadcast"} · ${venueOwnership.venueable.name}`}
            name={name}
            setName={setName}
            onSave={saveName}
            onCancel={() => setName(broadcast.name)}
            onClose={() => {
              flushSmsSave.current?.();
              waitForBroadcastSaves().then(() => { window.location.href = `/teams/${team.id}/broadcasts`; });
            }}
          />
        </div>

        <ProgressTrack ariaLabel="Broadcast steps" steps={trackSteps} step={step} completed={completed} onSelect={setStep} />

        <div style={styles.actions}>
          <Menu
            placement="bottom-end"
            items={[
              ...(isSms ? [] : [{ label: "Save as Blueprint", icon: LayersThree01, onClick: () => setSavingBlueprint(true) }]),
              { label: "Duplicate", icon: Copy01, onClick: duplicate },
              { label: "Delete", icon: Trash01, destructive: true, onClick: () => setConfirmingDelete(true) },
            ]}
            trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label="Broadcast actions" />}
          />
          {stepIndex > 0 && (
            <Button variant="secondary" onClick={() => setStep(steps[stepIndex - 1].value)}>
              Back
            </Button>
          )}
          {!isLastStep && (
            <Button
              variant="primary"
              trailingIcon={ArrowRight}
              disabled={nextDisabled}
              onClick={() => setStep(steps[stepIndex + 1].value)}
            >
              Next: {steps[stepIndex + 1].label}
            </Button>
          )}
          {isLastStep && sentMessage && (
            <Button variant="primary" onClick={() => { window.location.href = `/teams/${team.id}/broadcasts`; }}>
              Done
            </Button>
          )}
          {isLastStep && !sentMessage && (
            <Button variant="primary" disabled={!canSend} onClick={() => setConfirmingSend(true)}>
              {delivery === "now" ? "Send now" : "Schedule"}
            </Button>
          )}
        </div>
      </header>

      {failedSend && !scheduledSend && !sentMessage && (
        <FailedSendAlert failedSend={failedSend} onReschedule={() => setStep("schedule")} />
      )}

      <main style={styles.body}>
        {step === "content" ? (
          <section style={styles.contentArea}>
            <ContentStep heading={stepHeading} />
          </section>
        ) : (
          <>
            <section style={styles.formColumn}>
              {stepHeading}
              {step === "recipients" && <RecipientsStep />}
              {step === "message" && <MessageStep draft={draft} setDraft={setDraft} rendered={rendered} flushRef={flushSmsSave} />}
              {step === "sender" && (
                <SenderStep editedNames={editedSenderNames} setEditedNames={setEditedSenderNames} />
              )}
              {step === "schedule" && sentMessage && (
                <div style={{ marginTop: 24 }}>
                  <Alert color="success" title={sentMessage} />
                </div>
              )}
              {step === "schedule" && !sentMessage && (
                <ScheduleStep completed={completed} delivery={delivery} setDelivery={setDelivery} setStep={setStep} rendered={rendered} />
              )}
            </section>
            <aside style={styles.previewPane} aria-label={isSms ? "Phone preview" : "Inbox preview"}>
              {isSms
                ? <PhonePreview draft={draft} rendered={rendered} />
                : <InboxPreview hasRecipients={completed.recipients} onEditDesign={() => setStep("content")} />}
            </aside>
          </>
        )}
      </main>

      <Modal
        open={confirmingSend}
        onClose={cancelSend}
        onCancel={cancelSend}
        onConfirm={send}
        type={delivery === "now" ? "warning" : "brand"}
        icon={delivery === "now" ? undefined : false}
        title={delivery === "now" ? "Send this broadcast now?" : `Schedule for ${deliveryText}?`}
        description={delivery === "now"
          ? `It goes out right away to ${recipientsCount} fans. Once it starts sending, it can't be stopped or edited.`
          : "You can change or cancel it until it starts sending."}
        confirmLabel={delivery === "now" ? "Yes, send now" : "Schedule"}
        cancelLabel="Keep editing"
      >
        {delivery === "now" && (
          <dl style={styles.sendSummary}>
            <dt>From</dt>
            <dd>
              {isSms
                ? senderLabel(twilioMessagingServices?.find((service) => service.id === broadcast.twilio_template?.twilio_messaging_service_id))
                : <>{emailSendDomain?.name} &lt;{selectedSendDomain?.full_from_address}&gt;</>}
            </dd>
            <dt>{isSms ? "Length" : "Subject"}</dt>
            <dd>{isSms ? `${smsSegments(rendered.text ?? draft).segments} segments` : broadcast.subject}</dd>
            <dt>To</dt>
            <dd>{recipientsCount} fans</dd>
          </dl>
        )}
      </Modal>

      <Modal
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onCancel={() => setConfirmingDelete(false)}
        onConfirm={destroy}
        type="error"
        title="Delete this broadcast?"
        description="This can't be undone."
        confirmLabel="Delete"
        cancelLabel="Keep it"
      />

      {!isSms && (
        <SaveAsBlueprintModal
          isOpen={savingBlueprint}
          onClose={() => setSavingBlueprint(false)}
          template={template}
          copiedFromBlueprint={broadcast.beefree_template?.copied_from_blueprint}
          teamId={team.id}
          createBlueprint={createBlueprint}
          updateBlueprint={updateBlueprint}
        />
      )}
    </div>
  );
};

export default BroadcastComposer;
